package org.example.project.firebase

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import org.example.project.model.User
import kotlin.js.Date

object FirestoreManager {

    private val db = Firebase.firestore

    suspend fun createUser(
        user: User
    ) {
        if (user.uid.isEmpty()) {
            println("ERROR: Cannot create user with empty UID")
            return
        }

        db.collection("users")
            .document(user.uid)
            .set(
                mapOf(
                    "uid" to user.uid,
                    "email" to user.email,
                    "username" to user.username,
                    "photoUrl" to user.photoUrl,
                    "wins" to user.wins,
                    "losses" to user.losses,
                    "score" to user.score,
                    "gamesPlayed" to user.gamesPlayed,
                    "rating" to user.rating,
                    "online" to user.online,
                    "inGame" to user.inGame,
                    "lastSeen" to user.lastSeen
                )
            )
    }
    suspend fun getUser(uid: String): User? {
        return try {

            println("===== GET USER =====")
            println("Requested uid = $uid")

            val document = db.collection("users")
                .document(uid)
                .get()

            println("Exists = ${document.exists}")

            if (!document.exists) {
                return null
            }

            val user = document.data<User>()

            println("Deserialized user = $user")

            user

        } catch (e: Exception) {
            e.printStackTrace()
            null
        }
    }

    suspend fun getCurrentUser(): User? {
        println("getCurrentUser called")
        val uid = AuthManager.waitForUserId()
        println("getCurrentUser - currentUserId: $uid")

        if (uid == null || uid.isEmpty()) {
            println("ERROR: User not authenticated in getCurrentUser")
            return null
        }

        AuthManager.waitForAuthToken()

        return getUser(uid)
    }

    suspend fun updateUser(
        user: User
    ) {

        db.collection("users")
            .document(user.uid)
            .set(user)
    }

    suspend fun getPlayersInQueue(): Int {
        return Firebase.firestore
            .collection("matchmaking")
            .get()
            .documents
            .size
    }

    suspend fun setOnline(
        uid: String,
        online: Boolean
    ) {
        println("setOnline called - uid: $uid, online: $online")

        // Refresh auth token before Firestore operation
        val tokenRefreshed = AuthManager.refreshAuthToken()
        println("Token refresh result in setOnline: $tokenRefreshed")

        db.collection("users")
            .document(uid)
            .set(
                mapOf("online" to online),
                merge = true
            )
    }


    suspend fun setInGame(
        uid: String,
        inGame: Boolean
    ) {
        if (uid.isEmpty()) {
            println("ERROR: Empty UID in setInGame")
            return
        }
        db.collection("users")
            .document(uid)
            .set(
                mapOf("inGame" to inGame),
                merge = true
            )
    }

    suspend fun updateStats(
        uid: String,
        won: Boolean,
        scoreDelta: Int,
        ratingDelta: Int
    ) {

        val user = getUser(uid) ?: return

        updateUser(
            user.copy(
                wins = if (won) user.wins + 1 else user.wins,
                losses = if (!won) user.losses + 1 else user.losses,
                gamesPlayed = user.gamesPlayed + 1,
                score = user.score + scoreDelta,
                rating = (user.rating + ratingDelta).coerceAtLeast(0)
            )
        )
    }

    suspend fun ensureUserProfile(
        uid: String,
        email: String,
        username: String,
        photoUrl: String = ""
    ) {
        if (username.isBlank() || photoUrl.isBlank()) {
            println("Invalid GitHub profile data. Not creating user.")
            return
        }
        val existingUser = getUser(uid)
        if (existingUser == null) {
            createUser(
                User(
                    uid = uid,
                    email = email,
                    username = username,
                    photoUrl = photoUrl,
                    score = 1000
                )
            )
        } else {
            // Update existing user if GitHub data is provided and fields are empty
            if (photoUrl.isNotEmpty() && existingUser.photoUrl.isEmpty()) {
                updateUser(
                    existingUser.copy(photoUrl = photoUrl)
                )
            }
            if (username.isNotEmpty() && (existingUser.username.isEmpty() || existingUser.username == email.substringBefore("@"))) {
                updateUser(
                    existingUser.copy(username = username)
                )
            }
        }
    }

    suspend fun getLeaderboard(limit: Int = 50): List<User> {
        return db.collection("users")
            .get()
            .documents
            .mapNotNull { doc ->
                runCatching { doc.data<User>() }.getOrNull()
            }
            .filter { it.gamesPlayed > 0 }
            .sortedByDescending { it.score }
            .take(limit)
    }

    suspend fun getUserRank(uid: String): Int {
        val users = getLeaderboard(Int.MAX_VALUE)

        return users.indexOfFirst { it.uid == uid } + 1
    }
}